import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  Form,
  FormButton,
  IconTooltip,
  ImageUploader,
  Input,
  normalizeUrl,
  SelectInput,
  SliderInput,
  string,
  SwitchInput,
  url,
  useNotifications
} from '@ijoin/components';
import {
  getGetAdminAdvisoryOrganizationWhiteLabelsQueryKey,
  getGetWhitelabelByIdQueryKey,
  PatchWhitelabelBody,
  UploadAssetBodyContentType,
  useGetAdminAdvisoryOrganizationWhiteLabels,
  useGetWhitelabelById,
  usePatchWhitelabel,
  usePostAdminAdvisoryOrganizationWhiteLabel,
  useUploadAsset
} from '@ijoin/hooks';
import { Close, InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { assetUrl } from '../helpers';

const themeOptions: { label: string; value: string }[] = [
  { label: 'Blue', value: 'defaultBlue' },
  { label: 'Blue Alternate', value: 'alternateBlue' },
  { label: 'CON', value: 'con' },
  { label: 'Dark Blue', value: 'ijoin' },
  { label: 'Demo', value: 'demo' },
  { label: 'EF', value: 'ef' },
  { label: 'EX', value: 'ex' },
  { label: 'Green', value: 'defaultGreen' },
  { label: 'Green Alternate', value: 'alternateGreen' },
  { label: 'iJoin Default', value: 'relius' },
  { label: 'LDI', value: 'ldi' },
  { label: 'Nebraska', value: 'ameritas' },
  { label: 'NextLevel', value: 'nextlevel' },
  { label: 'Philadelphia', value: 'philadelphia' },
  { label: 'Racine', value: 'racine' },
  { label: 'Red', value: 'defaultRed' },
  { label: 'Red Alternate', value: 'alternateRed' },
  { label: 'St. Paul', value: 'stpaul' },
  { label: 'WG', value: 'wg' }
];

type OverviewVideoSource = 'custom' | 'default' | 'none';

const overviewVideoOptions: { label: string; value: OverviewVideoSource }[] = [
  { label: 'None', value: 'none' },
  { label: 'Default', value: 'default' },
  { label: 'Custom', value: 'custom' }
];

type FormProps = {
  additionalDisclosures: string;
  customOverviewVideo: string;
  displaySurveyScreen: boolean;
  isDefault: boolean;
  logoHeight: null | number | string;
  logoUri: string;
  name: string;
  nonSpousalDisclaimer: string;
  overviewVideo: string;
  overviewVideoSource: OverviewVideoSource;
  prpContactsEmail: string;
  prpContactsPhone: string;
  prpContactsUseCustom: boolean;
  recordkeeperDisplayName: string;
  returnUrl: string;
  subdomain: string;
  theme: string;
};

const resolver = zodResolver(
  z.object({
    additionalDisclosures: string(),
    customOverviewVideo: url(),
    displaySurveyScreen: z.boolean(),
    isDefault: z.boolean(),
    logoHeight: z
      .union([string(), z.number({ message: 'general.validations.invalid' })])
      .nullable(),
    logoUri: string(),
    name: string({ required: true }),
    nonSpousalDisclaimer: string(),
    overviewVideo: url(),
    overviewVideoSource: z.enum(['none', 'default', 'custom']),
    prpContactsEmail: string(),
    prpContactsPhone: string(),
    prpContactsUseCustom: z.boolean(),
    recordkeeperDisplayName: string(),
    returnUrl: url(),
    subdomain: string({ required: true }),
    theme: string({ required: true })
  })
);

type WhiteLabelEditDialogProps = {
  advisoryOrganizationId: string;
  isOpen: boolean;
  onClose: () => void;
  whiteLabelId?: string;
};

export const WhiteLabelEditDialog: FC<WhiteLabelEditDialogProps> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const fullScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const whitelabel = useGetWhitelabelById(props.whiteLabelId ?? '', {
    query: { enabled: !!props.whiteLabelId && props.isOpen }
  });

  const orgWhitelabels = useGetAdminAdvisoryOrganizationWhiteLabels(
    props.advisoryOrganizationId,
    { query: { enabled: props.isOpen } }
  );

  const otherDefault = orgWhitelabels.data?.find(
    w => w.isDefault && w.whiteLabelId !== props.whiteLabelId
  );

  const patchWhitelabel = usePatchWhitelabel({
    mutation: {
      onError: () => notifications.error('Failed to update white label!')
    }
  });

  const postWhitelabel = usePostAdminAdvisoryOrganizationWhiteLabel({
    mutation: {
      onError: () => notifications.error('Failed to create white label!')
    }
  });

  const uploadAsset = useUploadAsset({
    mutation: {
      onError: () => notifications.error('Failed to upload logo!')
    }
  });

  const defaultValues: FormProps = {
    additionalDisclosures: whitelabel.data?.additionalDisclosures ?? '',
    customOverviewVideo: normalizeUrl(whitelabel.data?.customOverviewVideo),
    displaySurveyScreen: whitelabel.data?.displaySurveyScreen ?? false,
    isDefault: whitelabel.data?.isDefault ?? false,
    logoHeight: whitelabel.data?.logoHeight ?? null,
    logoUri: whitelabel.data?.logoUri ?? '',
    name: whitelabel.data?.name ?? '',
    nonSpousalDisclaimer: whitelabel.data?.nonSpousalDisclaimer ?? '',
    overviewVideo: normalizeUrl(whitelabel.data?.overviewVideo),
    overviewVideoSource: !whitelabel.data?.displayOverviewVideo
      ? 'none'
      : whitelabel.data.useDefaultOverviewVideo
        ? 'default'
        : 'custom',
    prpContactsEmail: whitelabel.data?.prpContactsEmail ?? '',
    prpContactsPhone: whitelabel.data?.prpContactsPhone ?? '',
    prpContactsUseCustom: whitelabel.data?.prpContactsUseCustom ?? false,
    recordkeeperDisplayName: whitelabel.data?.recordkeeperDisplayName ?? '',
    returnUrl: normalizeUrl(whitelabel.data?.returnUrl),
    subdomain: whitelabel.data?.subdomain ?? '',
    theme: whitelabel.data?.theme ?? themeOptions[0].value
  };

  const methods = useForm<FormProps>({
    defaultValues,
    mode: 'all',
    resolver,
    values: defaultValues
  });

  const isDefault = methods.watch('isDefault');
  const logoUri = methods.watch('logoUri');
  const logoHeight = methods.watch('logoHeight');
  const overviewVideoSource = methods.watch('overviewVideoSource');
  const prpContactsUseCustom = methods.watch('prpContactsUseCustom');
  const subdomain = methods.watch('subdomain');

  const willOverrideDefault = !!otherDefault && isDefault;

  useEffect(() => {
    const dupe = orgWhitelabels.data?.find(
      w =>
        w.subdomain &&
        w.subdomain === subdomain &&
        w.whiteLabelId !== props.whiteLabelId
    );
    if (dupe) {
      methods.setError('subdomain', {
        message: 'Subdomain is already in use',
        type: 'manual'
      });
    } else if (methods.formState.errors.subdomain?.type === 'manual') {
      methods.clearErrors('subdomain');
    }
  }, [orgWhitelabels.data, methods, props.whiteLabelId, subdomain]);

  const onSubmit = async (data: FormProps) => {
    const body: PatchWhitelabelBody = {
      additionalDisclosures: data.additionalDisclosures,
      customOverviewVideo: data.customOverviewVideo,
      displayOverviewVideo: data.overviewVideoSource !== 'none',
      displaySurveyScreen: data.displaySurveyScreen,
      isDefault: data.isDefault,
      logoHeight: Number(data.logoHeight),
      logoUri: data.logoUri,
      name: data.name,
      nonSpousalDisclaimer: data.nonSpousalDisclaimer,
      overviewVideo: data.overviewVideo,
      prpContactsEmail: data.prpContactsEmail,
      prpContactsPhone: data.prpContactsPhone,
      prpContactsUseCustom: data.prpContactsUseCustom,
      recordkeeperDisplayName: data.recordkeeperDisplayName,
      returnUrl: data.returnUrl,
      subdomain: data.subdomain,
      theme: data.theme,
      useDefaultOverviewVideo: data.overviewVideoSource !== 'custom'
    };

    let whiteLabelId = props.whiteLabelId;
    try {
      if (!whiteLabelId) {
        const created = await postWhitelabel.mutateAsync({
          advisoryOrganizationId: props.advisoryOrganizationId,
          data: { name: data.name }
        });
        whiteLabelId = created.whiteLabelId;
        body.isDefault = data.isDefault || !!created.isDefault;
      }

      await patchWhitelabel.mutateAsync({
        data: body,
        whiteLabelId
      });
    } catch {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: getGetAdminAdvisoryOrganizationWhiteLabelsQueryKey(
        props.advisoryOrganizationId
      )
    });
    queryClient.invalidateQueries({
      queryKey: getGetWhitelabelByIdQueryKey(whiteLabelId)
    });

    notifications.success(
      props.whiteLabelId
        ? 'Changes saved successfully!'
        : 'White label created.'
    );
    methods.reset(data);
    props.onClose();
  };

  const onCancel = () => {
    methods.reset(defaultValues);
    props.onClose();
  };

  const isCreating = !props.whiteLabelId;
  const isUploading = uploadAsset.isPending;
  const isSaving = patchWhitelabel.isPending || postWhitelabel.isPending;
  const isLoading = !!props.whiteLabelId && whitelabel.isLoading;
  const isSaveDisabled =
    (!isCreating && !methods.formState.isDirty) || isUploading || isSaving;

  const previewSuffix = `.${window.location.host.replace(/^control-panel\./, '')}`;

  return (
    <Dialog
      data-testid='whiteLabelEditDialog'
      fullScreen={fullScreen}
      fullWidth
      isOpen={props.isOpen}
      maxWidth='md'
      onClose={onCancel}>
      <DialogTitle
        component='div'
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          pl: 3,
          pr: 1,
          py: 2
        }}>
        <Typography variant='h3'>
          {isCreating ? 'Create White Label' : 'Customize White Label'}
        </Typography>
        <IconButton
          aria-label='Close'
          data-testid='closeWhiteLabelDialog'
          onClick={onCancel}>
          <Close />
        </IconButton>
      </DialogTitle>
      {isLoading ? (
        <DialogContent dividers>
          <Stack alignItems='center' justifyContent='center' sx={{ p: 6 }}>
            <CircularProgress />
          </Stack>
        </DialogContent>
      ) : (
        <Form methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Typography variant='h4'>General</Typography>
                <Divider />
                <Grid container spacing={2}>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <Input
                      format='string'
                      fullWidth
                      label='Name'
                      name='name'
                      required
                      size='small'
                    />
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <Input
                      endAdornment={
                        <InputAdornment position='end'>
                          {previewSuffix}
                        </InputAdornment>
                      }
                      format='string'
                      fullWidth
                      label='Subdomain'
                      name='subdomain'
                      required
                      size='small'
                    />
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <SelectInput
                      fullWidth
                      label='Theme'
                      name='theme'
                      options={themeOptions}
                      required
                      size='small'
                    />
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <Input
                      format='string'
                      fullWidth
                      label='Recordkeeper Display Name'
                      labelInfo={
                        <>
                          Public-facing recordkeeper name shown in the
                          affiliation sentence on participant PRP enrollment
                          reports (BlackRock, IRA, Leafhouse, PGIM, Stadion, UT,
                          generic PRP), e.g.,{' '}
                          <strong>
                            &quot;{'{name}'} is not affiliated with iJoin.
                            Representatives of {'{name}'} do not provide tax or
                            legal advice.&quot;
                          </strong>{' '}
                          If left blank, the report renders the default
                          disclaimer/footer layout without the affiliation
                          sentence.
                        </>
                      }
                      name='recordkeeperDisplayName'
                      size='small'
                    />
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <Input
                      format='url'
                      fullWidth
                      label='Return URL'
                      labelInfo='Where participants are sent after they log out or finish an enrollment flow. Also substituted into the "Return to home" link in canned campaign emails. Leave blank to fall back to the SSO record-keeper URL.'
                      name='returnUrl'
                      size='small'
                    />
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <Stack spacing={1}>
                      <SwitchInput
                        color='success'
                        disabled={whitelabel.data?.isDefault}
                        hideError
                        label='Default'
                        name='isDefault'
                        off='Off'
                        on='On'
                        tooltip='Set another white label as the default to unset this one'
                        tooltipIcon={
                          whitelabel.data?.isDefault && (
                            <InfoOutlined fontSize='small' />
                          )
                        }
                      />
                      {willOverrideDefault && (
                        <Alert
                          data-testid='isDefaultOverrideWarning'
                          severity='warning'>
                          {`"${otherDefault?.name ?? 'Another white label'}" is currently set as the default. Saving will unset it and make this white label the new default.`}
                        </Alert>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Branding</Typography>
                <Divider />
                <Grid alignItems='center' container spacing={3}>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <Stack alignItems='center' spacing={1}>
                      <Stack
                        alignItems='center'
                        direction='row'
                        spacing={0.5}
                        sx={{ alignSelf: 'flex-start' }}>
                        <Typography variant='subtitle2'>Logo</Typography>
                        <IconTooltip aria-label='Logo more info'>
                          Whitelabel logo image. Rendered on the participant
                          login screen, on PRP and enrollment report
                          covers/headers (BlackRock, IRA, Leafhouse, PGIM,
                          Stadion, UT, default), on plan health reports and plan
                          cards in Advisor, and substituted into canned campaign
                          emails.
                        </IconTooltip>
                      </Stack>
                      <ImageUploader
                        alt='Logo preview'
                        data-testid='logoUri'
                        emptyPlaceholder='No logo uploaded'
                        innerImageSx={{
                          height: logoHeight ? Number(logoHeight) : 40
                        }}
                        invalidTypeMessage='Logo must be a PNG, JPEG, or WebP image.'
                        isUploading={isUploading}
                        label='Upload logo'
                        onChange={key =>
                          methods.setValue('logoUri', key, {
                            shouldDirty: true,
                            shouldValidate: true
                          })
                        }
                        onUpload={({ content, contentType }) =>
                          uploadAsset.mutateAsync({
                            data: {
                              content,
                              contentType:
                                contentType as UploadAssetBodyContentType
                            },
                            kind: 'logos'
                          })
                        }
                        previewSrc={logoUri ? assetUrl(logoUri) : undefined}
                        previewVariant='image'
                        registerCtx={methods.register('logoUri')}
                        uploadingLabel='Uploading...'
                        value={logoUri}
                      />
                    </Stack>
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <SliderInput
                      format='number'
                      label='Logo Height (px)'
                      max={250}
                      min={20}
                      name='logoHeight'
                      size='small'
                    />
                  </Grid>
                </Grid>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Enrollment Experience</Typography>
                <Divider />
                <Stack direction={{ md: 'row', xs: 'column' }} spacing={2}>
                  <SelectInput
                    fullWidth
                    label='Overview Video'
                    labelInfo='Controls the intro video on the participant overview page. None hides the video, Default plays the URL configured below, Custom plays the URL configured below. Managed-account methodologies can override this with their own video.'
                    name='overviewVideoSource'
                    options={overviewVideoOptions}
                    size='small'
                  />
                  {overviewVideoSource === 'default' && (
                    <Input
                      format='url'
                      fullWidth
                      label='Default Overview Video URL'
                      labelInfo='Whitelabel default video shown on participant overview when no managed-account methodology supplies its own. Falls back to the system iJoin Wistia video if blank.'
                      name='overviewVideo'
                      size='small'
                    />
                  )}
                  {overviewVideoSource === 'custom' && (
                    <Input
                      format='url'
                      fullWidth
                      label='Custom Overview Video URL'
                      labelInfo='Custom video URL shown on the participant overview page when Overview Video is set to Custom. Use the embed iframe URL (e.g. fast.wistia.net/embed/iframe/...).'
                      name='customOverviewVideo'
                      size='small'
                    />
                  )}
                </Stack>
                <SwitchInput
                  color='success'
                  hideError
                  label='Display Survey Screen'
                  name='displaySurveyScreen'
                  off='Off'
                  on='On'
                  tooltip='When on, participants land on the satisfaction survey screen after completing enrollment or strategy authorization. When off, they go straight to the thank-you screen. Hub-and-spoke flows always skip the survey.'
                  tooltipIcon={<InfoOutlined />}
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>PRP Contacts</Typography>
                <Divider />
                <SwitchInput
                  color='success'
                  hideError
                  label='Use Custom PRP Contacts'
                  name='prpContactsUseCustom'
                  off='Use Advisor Contacts'
                  on='Use Custom Contacts'
                  tooltip='When off, PRP enrollment reports show the advisor record. When on, the report shows the custom phone and email below.'
                  tooltipIcon={<InfoOutlined />}
                />
                {prpContactsUseCustom && (
                  <Stack direction={{ md: 'row', xs: 'column' }} spacing={2}>
                    <Input
                      format='phone'
                      fullWidth
                      label='Phone'
                      labelInfo='Phone number printed in the contact block of PRP enrollment reports (BlackRock, IRA, Leafhouse, PGIM, UT, Fingage, generic PRP) when Use Custom PRP Contacts is on.'
                      name='prpContactsPhone'
                      size='small'
                    />
                    <Input
                      format='string'
                      fullWidth
                      label='Email / Contact Info'
                      labelInfo='Free-form text shown in the contact block of PRP enrollment reports and the participant Thank You page when Use Custom PRP Contacts is on. Accepts an email address or any other contact info (e.g. hours of operation).'
                      name='prpContactsEmail'
                      size='small'
                    />
                  </Stack>
                )}
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Disclosures</Typography>
                <Divider />
                <Input
                  format='string'
                  fullWidth
                  label='Additional Disclosures'
                  labelInfo='Rendered as HTML in the Associated Disclosures modal during the strategy authorization step. HTML tags are allowed; leave blank to omit the additional-disclosures block from that modal.'
                  multiline
                  name='additionalDisclosures'
                  rows={6}
                  size='small'
                />
                <Input
                  format='string'
                  fullWidth
                  label='Non-Spousal Disclaimer'
                  labelInfo='Rendered as HTML in the beneficiary flow when a participant designates a non-spouse primary beneficiary. HTML tags are allowed; if blank, the non-spousal disclaimer modal is not shown.'
                  multiline
                  name='nonSpousalDisclaimer'
                  rows={6}
                  size='small'
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button data-testid='cancel' onClick={onCancel} variant='outlined'>
              Cancel
            </Button>
            <FormButton data-testid='save' disabled={isSaveDisabled}>
              Save
            </FormButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
};

WhiteLabelEditDialog.displayName = 'WhiteLabelEditDialog';
